import { useCallback, useEffect, useMemo, useState } from 'react';

import { PROGRAM_CODE_MAP, resolveProgramCode } from '../../utils/dataNormalizer';
import { formatVmtSendErrorMessage } from '../../utils/vmtSendError';
import { buildVmtRecordFromSelection, chunkRecords, sanitizeRecords, validateVmtRecord } from '../../utils/vmt';
import { resolveDiStatusValue, resolveSelectionForStaging } from '../../utils/productSelectionsRow';
import {
  buildSyncRefreshPlan,
  isUnknownDiValue,
  syncThrottleDelayMs,
  SYNC_REFRESH_BAC_THRESHOLD,
} from '../../utils/productSelectionsSync';

const useProductSelectionsSync = ({
  selectedRows,
  buildVmtContextFromRow,
  sendMutation,
  userEmail,
  syncRefreshMode,
  onNotify,
  refreshDealerSelections,
  forceProgramSyncMutation,
  setSnackbar,
}) => {
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncRows, setSyncRows] = useState([]);
  const [syncQueue, setSyncQueue] = useState([]);
  const [syncProgress, setSyncProgress] = useState({ processed: 0, total: 0 });
  const [syncSending, setSyncSending] = useState(false);
  const [syncRefreshPlanState, setSyncRefreshPlanState] = useState(null);
  const [syncRefreshInFlight, setSyncRefreshInFlight] = useState(false);

  const updateSyncRows = useCallback((updater) => {
    setSyncRows((prev) => prev.map((item) => updater(item)));
  }, []);

  const buildSyncItem = useCallback(
    (row) => {
      const id = row?.id != null ? String(row.id) : '';
      const referenceId = row?.referenceId != null ? String(row.referenceId).trim() : '';
      const diStatusRaw = resolveDiStatusValue(row, row?.di || null) || row?.diStatusResolved || row?.diStatus || '';
      const diSelectionDate = row?.diSelectionDate ?? row?.di?.diSelectionDate ?? '';
      const diEffectiveDate = row?.diEffectiveDate ?? row?.di?.diEffectiveDate ?? '';
      const missing = [];
      if (isUnknownDiValue(diStatusRaw)) missing.push('DI Status');
      if (isUnknownDiValue(diSelectionDate)) missing.push('DI Selection Date');
      if (isUnknownDiValue(diEffectiveDate)) missing.push('DI Effective Date');

      if (!id) {
        return {
          id,
          referenceId,
          row,
          diStatus: diStatusRaw,
          diSelectionDate,
          diEffectiveDate,
          status: 'skipped',
          reason: 'Missing row id.',
        };
      }

      if (missing.length > 0) {
        return {
          id,
          referenceId,
          row,
          diStatus: diStatusRaw,
          diSelectionDate,
          diEffectiveDate,
          status: 'skipped',
          reason: `Missing/unknown ${missing.join(', ')}.`,
        };
      }

      const selection = resolveSelectionForStaging(row);
      if (!selection) {
        return {
          id,
          referenceId,
          row,
          diStatus: diStatusRaw,
          diSelectionDate,
          diEffectiveDate,
          status: 'skipped',
          reason: 'Missing selection payload.',
        };
      }

      const diStatus = String(diStatusRaw || '').trim();
      const selectionForSync = {
        ...selection,
        productSelectionStatus: diStatus,
        productSelectionStatusCd: diStatus,
        status: diStatus,
        selectionStatus: diStatus,
        selectionDate: diSelectionDate,
        productSelectionDate: diSelectionDate,
        billingDate: diSelectionDate,
        effectiveDate: diEffectiveDate,
      };
      const record = buildVmtRecordFromSelection(selectionForSync, buildVmtContextFromRow(row));
      const sanitized = sanitizeRecords([record])[0];
      const validation = validateVmtRecord(sanitized);
      if (Object.keys(validation).length > 0) {
        const fields = Object.keys(validation).join(', ');
        return {
          id,
          referenceId,
          row,
          diStatus: diStatusRaw,
          diSelectionDate,
          diEffectiveDate,
          status: 'skipped',
          reason: `Invalid VMT record: ${fields}.`,
        };
      }

      return {
        id,
        referenceId,
        row,
        diStatus: diStatusRaw,
        diSelectionDate,
        diEffectiveDate,
        status: 'queued',
        reason: '',
        record: sanitized,
      };
    },
    [buildVmtContextFromRow]
  );

  const handleSyncSelections = useCallback(() => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: 'Select at least one row to sync.', severity: 'info' });
      return;
    }
    const items = selectedRows.map((row) => buildSyncItem(row));
    const sendable = items.filter((item) => item.status === 'queued' && item.record);
    const chunks = chunkRecords(sendable, 10).map((chunk) => ({
      rowIds: chunk.map((item) => item.id),
      records: chunk.map((item) => item.record),
      referenceIds: chunk.map((item) => item.referenceId).filter(Boolean),
    }));
    const refreshPlan = buildSyncRefreshPlan(selectedRows);
    setSyncRefreshPlanState(
      sendable.length > 0 && refreshPlan.targets.length > 0 ? { ...refreshPlan, mode: syncRefreshMode } : null
    );
    setSyncRows(items);
    setSyncQueue(chunks);
    setSyncProgress({ processed: 0, total: chunks.length });
    setSyncModalOpen(true);
    setSyncSending(false);
  }, [buildSyncItem, selectedRows, setSnackbar, syncRefreshMode]);

  const extractSyncErrorMap = useCallback((error) => {
    const data = error?.response?.data;
    const payload = data?.error || data;
    const processed = Array.isArray(payload?.processedErrorRecords) ? payload.processedErrorRecords : [];
    const validation = Array.isArray(payload?.validationErrorRecords) ? payload.validationErrorRecords : [];
    const records = [...processed, ...validation];
    const map = new Map();
    records.forEach((record) => {
      const ref = record?.referenceId || record?.referenceID || record?.Id || record?.id || '';
      const message = String(record?.errorMessage || record?.message || '').trim();
      if (ref) {
        map.set(String(ref).trim(), message || 'Record rejected.');
      }
    });
    return map;
  }, []);

  useEffect(() => {
    if (syncSending || syncQueue.length === 0) return;
    const nextChunk = syncQueue[0];
    const rowIds = new Set(nextChunk.rowIds || []);
    updateSyncRows((item) => {
      if (!rowIds.has(item.id)) return item;
      return { ...item, status: 'sending', reason: '' };
    });
    setSyncSending(true);
    sendMutation.mutate(
      {
        records: nextChunk.records,
        userEmail: userEmail || null,
      },
      {
        onSuccess: () => {
          updateSyncRows((item) => {
            if (!rowIds.has(item.id)) return item;
            return { ...item, status: 'success', reason: '' };
          });
        },
        onError: (error) => {
          const errorMap = extractSyncErrorMap(error);
          const fallback = formatVmtSendErrorMessage(error);
          updateSyncRows((item) => {
            if (!rowIds.has(item.id)) return item;
            const byRef = item.referenceId ? errorMap.get(item.referenceId) : null;
            return { ...item, status: 'failed', reason: byRef || fallback };
          });
        },
        onSettled: () => {
          setSyncQueue((prev) => prev.slice(1));
          setSyncProgress((prev) => ({
            processed: prev.processed + 1,
            total: prev.total,
          }));
          setTimeout(() => setSyncSending(false), syncThrottleDelayMs);
        },
      }
    );
  }, [extractSyncErrorMap, sendMutation, syncQueue, syncSending, updateSyncRows, userEmail]);

  useEffect(() => {
    if (!syncRefreshPlanState) return;
    if (syncQueue.length !== 0 || syncSending || syncRefreshInFlight) return;

    const runRefreshPlan = async () => {
      setSyncRefreshInFlight(true);
      const { targets = [], uniqueBacCount = 0, mode } = syncRefreshPlanState || {};
      if (targets.length === 0) {
        setSyncRefreshPlanState(null);
        setSyncRefreshInFlight(false);
        return;
      }

      const shouldUseDetailNotify = mode === 'detail' && typeof onNotify === 'function';
      if (shouldUseDetailNotify) {
        targets.forEach((target) => {
          onNotify?.('DI sync completed.', 'success', {
            refreshDealer: true,
            refreshProgramName: target.programName,
            refreshBac: target.bac,
            refreshDelayMs: 0,
            suppressSnackbar: true,
          });
        });
        setSyncRefreshPlanState(null);
        setSyncRefreshInFlight(false);
        return;
      }

      if (uniqueBacCount > 0 && uniqueBacCount < SYNC_REFRESH_BAC_THRESHOLD) {
        for (const target of targets) {
          try {
            // Sequential refresh so we don't overload the single-dealer endpoint.
            await refreshDealerSelections({ programName: target.programName, bac: target.bac });
          } catch (error) {
            console.error('Failed to refresh dealer after DI sync', error);
          }
        }
      } else {
        const programs = Array.from(
          new Set(
            targets
              .map((target) => resolveProgramCode(target.programName) ?? PROGRAM_CODE_MAP[target.programName] ?? target.programName)
              .filter(Boolean)
          )
        );
        programs.forEach((programName) => {
          forceProgramSyncMutation.mutate({ programName });
        });
      }

      setSyncRefreshPlanState(null);
      setSyncRefreshInFlight(false);
    };

    void runRefreshPlan();
  }, [
    forceProgramSyncMutation,
    onNotify,
    refreshDealerSelections,
    syncQueue.length,
    syncRefreshInFlight,
    syncRefreshPlanState,
    syncSending,
  ]);

  const syncCounts = useMemo(() => {
    const counts = { total: syncRows.length, queued: 0, sending: 0, success: 0, failed: 0, skipped: 0 };
    syncRows.forEach((item) => {
      if (item.status && Object.prototype.hasOwnProperty.call(counts, item.status)) {
        counts[item.status] += 1;
      }
    });
    return counts;
  }, [syncRows]);

  return {
    syncModalOpen,
    setSyncModalOpen,
    syncRows,
    syncQueue,
    syncProgress,
    syncSending,
    syncCounts,
    handleSyncSelections,
  };
};

export default useProductSelectionsSync;
